<template>
	<div class="transform" :style="transform">
		<div ref="obj" class="polaroid" :class="classes">
			<div class="polaroid__image">
				<Submission v-bind="submission" />
			</div>
			<div class="polaroid__text">
				<p class="polaroid__text__title" v-if="!isText">
					{{ submission.title }}
				</p>
				<p>
					<span class="polaroid__text__extra">submitted </span>by
					{{ name }}
				</p>
			</div>
		</div>
	</div>
</template>

<script>
import { mapActions } from 'vuex'
import { enums } from 'protoswipe-shared'
import Tween from '@/plugins/Animator/lib/Tween'
import { easeOutQuad, easeOutElastic } from '@/lib/easings'
import { pointDist } from '@/lib/trig'
import inputPos from '@/mixins/inputPos'
import Submission from '@/components/shared/Submission'

const coord = () => [0, 0]
const CENTER = coord()
const ANGLE_MAX = 5
const DX_THRESHOLD = 50
const DX_THRESHOLD_WALL = 75
const PHYS_THRESHOLD = 2
const GRAVITY = 2

const Action = {
	like: 'like',
	dislike: 'dislike'
}

// Helpers
const randBetween = (min, max) => Math.random() * (max - min) + min

export default {
	name: 'SubmissionPolaroid',
	props: {
		submission: Object,
		wall: {
			type: Boolean,
			default: false
		},
		viewOnly: {
			type: Boolean,
			default: false
		}
	},
	data() {
		const angleTarget = randBetween(-ANGLE_MAX, ANGLE_MAX)
		const angle = angleTarget + Math.sign(Math.random() - 0.5) * 50

		// Start off top of screen
		const pos = coord()
		pos[1] = -window.innerHeight

		return {
			interactive: false,
			dragging: false,
			opacity: 1,
			angle: this.viewOnly ? 0 : angle,
			angleTarget: this.viewOnly ? 0 : angleTarget,
			pos,
			dv: coord(),
			target: coord(),
			offset: coord(),
			action: undefined,
			voted: false,
			usePhysics: false
		}
	},
	computed: {
		isText() {
			return this.submission.campaign.type === enums.CampaignType.text
		},
		name() {
			return this.submission.nickname || 'Anonymous'
		},
		transform() {
			const [x, y] = this.pos
			return {
				opacity: this.opacity,
				transform: `translate3d(${x}px, ${y}px, 0) rotate(${this.angle}deg)`
			}
		},
		classes() {
			return {
				dragging: this.dragging,
				interactive: this.interactive,
				'view-only': this.viewOnly,
				[this.action]: this.action !== undefined
			}
		}
	},
	methods: {
		...mapActions('session', ['addVote']),
		onDown(event) {
			if (!this.dragging) {
				// Start drag
				event.preventDefault()
				this.dragging = true
				this.removeAllTweens()

				// Set target and calculate offset
				const { x, y } = this.inputPos(event)
				this.target = [x, y]
				this.offset = [x, y]
			}
		},
		onMove(event) {
			if (this.dragging) {
				// Update target position
				event.preventDefault()
				const { x, y } = this.inputPos(event)
				this.target = [x, y]
			}
		},
		onUp(event) {
			if (this.dragging) {
				if (event) event.preventDefault()
				this.dragging = false

				if (this.action !== undefined) {
					this.submitVote()
				} else {
					this.resetPosition()
				}
			}
		},
		submitVote() {
			// Mark as voted and non-interactive
			this.voted = true
			this.interactive = false
			const duration = 250

			// Decide whether to use physics to animate out
			const vel = pointDist([0, 0], this.dv)
			this.usePhysics = vel > PHYS_THRESHOLD

			// Animate out opacity
			this.addTween(
				{ from: 1, to: 0, duration },
				value => (this.opacity = value),
				() => {
					// Emit done event after opacity animation
					const id = this.submission.id
					const liked = this.action === Action.like
					this.$emit('vote', { id, liked })
				}
			)
		},
		resetPosition() {
			// Tween back to center
			this.addTween(
				{
					from: this.pos,
					to: CENTER,
					ease: easeOutElastic,
					type: Tween.TYPE_VEC2
				},
				pos => (this.pos = pos)
			)
			this.addTween(
				{
					from: this.angle,
					to: this.angleTarget,
					duration: 200,
					ease: easeOutQuad
				},
				angle => (this.angle = angle)
			)
		},

		// For triggering likes/dislikes externally
		trigger(liked) {
			// Check if currently in interactive state
			if (!this.interactive || this.voted) return false

			// Set velocity
			const k = liked ? 1 : -1
			this.dv[0] = randBetween(25, 35) * k
			this.dv[1] = randBetween(8, 12) * -1

			// Submit vote
			this.action = liked ? Action.like : Action.dislike
			this.submitVote()
		}
	},
	tick() {
		if (this.dragging) {
			// Calculate new position
			const x = this.target[0] - this.offset[0]
			const y = this.target[1] - this.offset[1]

			// Store x and y velocity
			this.dv = [x - this.pos[0], y - this.pos[1]]

			// Move to target position
			this.pos = [x, y]

			// Update action based on position
			const threshold = this.wall ? DX_THRESHOLD_WALL : DX_THRESHOLD
			if (x > threshold) {
				// Like
				this.action = Action.like
				if (this.wall) this.onUp()
			} else if (x < -threshold) {
				// Dislike
				this.action = Action.dislike
				if (this.wall) this.onUp()
			} else {
				this.action = undefined
			}
		} else if (this.voted && this.usePhysics) {
			// Move card
			this.pos[0] += this.dv[0]
			this.pos[1] += this.dv[1]
			// Apply gravity to dy
			this.dv[1] += GRAVITY
		}

		if (this.dragging || this.voted) {
			// Rotate more the further from x = 0 we go
			this.angle = this.angleTarget + this.pos[0] / 20
		}
	},
	mounted() {
		const opts = { passive: false }
		this.$refs.obj.addEventListener('mousedown', this.onDown, opts)
		this.$refs.obj.addEventListener('touchstart', this.onDown, opts)
		window.addEventListener('mousemove', this.onMove, opts)
		window.addEventListener('touchmove', this.onMove, opts)
		window.addEventListener('mouseup', this.onUp, opts)
		window.addEventListener('touchend', this.onUp, opts)

		// Enter animation
		const ease = easeOutQuad
		this.addTween(
			{ from: this.angle, to: this.angleTarget, ease },
			angle => (this.angle = angle)
		)
		this.addTween(
			{ from: this.pos[1], to: CENTER[1], ease },
			y => (this.pos = [this.pos[0], y]),
			() => (this.interactive = true)
		)
	},
	beforeDestroy() {
		this.$refs.obj.removeEventListener('mousedown', this.onDown)
		this.$refs.obj.removeEventListener('touchstart', this.onDown)
		window.removeEventListener('mousemove', this.onMove)
		window.removeEventListener('touchmove', this.onMove)
		window.removeEventListener('mouseup', this.onUp)
		window.removeEventListener('touchend', this.onUp)
	},
	mixins: [inputPos],
	components: {
		Submission
	}
}
</script>

<style lang="scss" scoped>
@import '~scss/vars';
@import '~scss/mq';

$ease: 0.15s $ease-out-sine;

@mixin pick-up($travel, $scale) {
	transform: translate(-50%, calc(-50% - #{$travel})) scale($scale);
	box-shadow: 0 $travel 0.75rem $travel * -0.3 rgba($black, 0.3);
}

.transform {
	position: absolute;
	top: 50%;
	left: 50%;
	width: 0;
	height: 0;
	z-index: 100;
}

.polaroid {
	position: absolute;
	top: 0;
	left: 0;
	transform: translate(-50%, -50%);

	padding: 1rem;
	width: calc(100vw - 6rem);
	max-width: 28rem;
	background-color: $white;
	box-shadow: 0 0.2rem 0.25rem rgba($black, 0.3);
	transition: transform $ease, box-shadow $ease;
	cursor: grab;
	user-select: none;
	pointer-events: none;

	&.interactive {
		pointer-events: all;
	}

	// Stamps
	&:not(.view-only) {
		&::before,
		&::after {
			position: absolute;
			top: 0.5rem;
			font-size: 2.5rem;
			font-weight: bold;
			padding: 0.25rem 1rem;
			border-width: 0.4rem;
			border-radius: 0.25rem;
			border-style: solid;
			background-color: rgba($white, 0.25);
			z-index: 1;
			opacity: 0;
			transition: opacity 0.1s linear;
		}

		&.dislike::before,
		&.like::after {
			opacity: 1;
		}

		// Dislike stamp
		&::before {
			content: 'NOPE';
			right: 0.25rem;
			color: $blue-73;
			border-color: $blue-73;
			transform: rotate(15deg);
			transform-origin: top left;
		}

		// Like stamp
		&::after {
			content: 'OK!';
			left: 0.25rem;
			color: $orange-95;
			border-color: $orange-95;
			transform: rotate(-15deg);
			transform-origin: top right;
		}
	}

	&__image {
		margin-bottom: 1rem;
	}
	&__text {
		color: $blue-48;
		text-align: right;
		> p {
			margin: 0;
			line-height: 1.5;
		}
		&__title {
			font-weight: bold;
		}
		&__extra {
			display: none;
		}
	}

	&.dragging {
		@include pick-up(0.75rem, 1.05);
		cursor: grabbing;
	}

	@include mq(xs) {
		width: calc(100vw - 8rem);
	}

	@include mq(md) {
		&.dragging {
			@include pick-up(1rem, 1.025);
		}

		&__text__extra {
			display: inline;
		}
	}
}
</style>
